import { FIELD_TO_COL, EXPECTED_FIELDS } from "../config/constants";

export const parseNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[$,%\s]/g, ""));
  return isNaN(n) ? 0 : n;
};
export const fmt = (n) => {
  if (n === 0 || n === null || n === undefined || isNaN(n)) return "$0";
  const abs  = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};
export const fmtNum = (n) => !n || isNaN(n) ? "0" : Math.round(n).toLocaleString();
export const pct = (n) => !n || isNaN(n) ? "0%" : `${(n * 100).toFixed(1)}%`;

/**
 * Converts a raw CSV row to a database-ready lead record.
 * @param {Object} row - Raw row from CSV mapping
 * @param {string} sourceFile - Source filename for tracking
 * @param {Array<Object>} qualFieldMapping - Qualification field config from org settings
 * @returns {Object} Database-ready lead record
 */
export function toDbRow(row, sourceFile, qualFieldMapping) {
  const r = {};
  EXPECTED_FIELDS.forEach((f) => { r[FIELD_TO_COL[f]] = row[f] || ""; });
  if (sourceFile) r.source_file = sourceFile;

  // Store qualification field values in custom_data JSONB
  if (qualFieldMapping && Object.keys(qualFieldMapping).length > 0) {
    const customData = {};
    Object.entries(qualFieldMapping).forEach(([fieldId, csvColumn]) => {
      const val = row[csvColumn];
      if (val !== undefined && val !== null && val !== "") {
        customData[fieldId] = val.trim ? val.trim() : val;
      }
    });
    if (Object.keys(customData).length > 0) {
      r.custom_data = customData;
    }
  }

  return r;
}
/**
 * Converts a database lead record to app display format.
 * @param {Object} row - Raw database row
 * @returns {Object} App-formatted lead record
 */
export function toAppRow(row) {
  const r = {};
  EXPECTED_FIELDS.forEach((f) => { r[f] = row[FIELD_TO_COL[f]] || ""; });
  r._source_file = row.source_file || "";
  // Restore qualification field values from custom_data
  if (row.custom_data && typeof row.custom_data === "object") {
    r._custom_data = row.custom_data;
  }
  return r;
}
export function normalizeBooth(val, boothTypes = []) {
  if (!val || !val.trim()) return "Unknown";
  const trimmed = val.trim();
  if (boothTypes.length > 0) {
    const norm  = trimmed.toLowerCase().replace(/\s+/g, "");
    const match = boothTypes.find(b =>
      norm.includes(b.name.toLowerCase().replace(/\s+/g, ""))
    );
    return match?.name || trimmed;
  }
  // Legacy fallback — defense template booth names
  const v = trimmed.toLowerCase().replace(/\s+/g, "");
  if (v.includes("10x10") && v.includes("p")) return "10x10 P";
  if (v.includes("10x20") && v.includes("p")) return "10x20 P";
  if (v.includes("10x10")) return "10x10 Booth";
  if (v.includes("10x20")) return "10x20 Booth";
  if (v.includes("20x20")) return "20x20 Booth";
  if (v.includes("table")) return "Table Setup";
  if (v.includes("kiosk")) return "Kiosk";
  return trimmed;
}
/**
 * Normalizes a raw ranking/grade value to a standard key.
 * @param {string} val - Raw ranking value from CSV or database
 * @returns {string} Normalized ranking key (e.g. "hot", "warm", "cold")
 */
export function normalizeRanking(val) {
  if (!val || !val.trim()) return "Unranked";
  if (val.includes("Hot")) return "Hot Lead for Follow Up";
  if (val.includes("Warm")) return "Warm for Nurture";
  if (val.includes("Cold")) return "Cold Exploration";
  return val.trim();
}
export function explodeField(rows, field) {
  const counts = {};
  rows.forEach((r) => {
    const val = (r[field] || "").trim();
    if (!val) { counts["None"] = (counts["None"] || 0) + 1; return; }
    val.split(";").forEach((v) => { const c = v.trim(); if (c) counts[c] = (counts[c] || 0) + 1; });
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}
/**
 * Parses a submission date string from various badge provider formats.
 * @param {string} val - Raw date string
 * @returns {string|null} ISO date string or null if unparseable
 */
export function parseSubmissionDate(val) {
  if (!val || !val.trim()) return null;
  let s = val.trim();
  s = s.replace(/(\d)(AM|PM)/i, "$1 $2");
  let d = new Date(s);
  if (isNaN(d.getTime())) { const dateOnly = s.split(/\s+/)[0]; d = new Date(dateOnly); }
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
/**
 * Calculates start/end dates from a date preset or custom range.
 * @param {string} preset - Date preset key (e.g. "last30", "thisQuarter", "fy")
 * @param {string} customFrom - Custom start date (ISO string)
 * @param {string} customTo - Custom end date (ISO string)
 * @param {number} fyStartMonth - Fiscal year start month (1-12)
 * @returns {{ from: string|null, to: string|null }} Date range
 */
export function getDateRange(preset, customFrom, customTo, fyStartMonth) {
  const fySM = fyStartMonth ?? 9;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const fyStart = m >= fySM ? new Date(y, fySM, 1) : new Date(y - 1, fySM, 1);
  const fyEnd = m >= fySM ? new Date(y + 1, fySM - 1, 30, 23, 59, 59) : new Date(y, fySM - 1, 30, 23, 59, 59);
  const lastFyStart = new Date(fyStart.getFullYear() - 1, fySM, 1);
  const lastFyEnd = new Date(fyStart.getFullYear(), fySM - 1, 30, 23, 59, 59);
  const qMonth = Math.floor(m / 3) * 3;
  switch (preset) {
    case "thisMonth": return [new Date(y, m, 1), new Date(y, m + 1, 0, 23, 59, 59)];
    case "lastMonth": return [new Date(y, m - 1, 1), new Date(y, m, 0, 23, 59, 59)];
    case "thisQuarter": return [new Date(y, qMonth, 1), new Date(y, qMonth + 3, 0, 23, 59, 59)];
    case "lastQuarter": return [new Date(y, qMonth - 3, 1), new Date(y, qMonth, 0, 23, 59, 59)];
    case "thisFY": return [fyStart, fyEnd];
    case "lastFY": return [lastFyStart, lastFyEnd];
    case "last90": return [new Date(now.getTime() - 90 * 86400000), now];
    case "last180": return [new Date(now.getTime() - 180 * 86400000), now];
    case "custom": {
      const from = customFrom ? new Date(customFrom + "T00:00:00") : null;
      const to = customTo ? new Date(customTo + "T23:59:59") : null;
      return [from, to];
    }
    default: return [null, null];
  }
}

// ─── Standard Field Column Variants (for first-pass auto-mapping) ───
const STANDARD_FIELD_VARIANTS = {
  "Primary Campaign Source": ["primary campaign source", "event", "event name", "campaign"],
  "Marketing Influenced": ["marketing influenced", "influenced"],
  "Submission Date": ["submission date", "submission date gmt", "date", "timestamp", "scan date"],
  "Sales Executive": ["sales executive", "sales rep", "rep", "owner", "assigned to"],
  "First Name": ["first name", "firstname", "first"],
  "Last Name": ["last name", "lastname", "last"],
  "Email": ["email", "email address", "e-mail"],
  "Phone": ["phone", "phone number", "mobile", "telephone"],
  "Company": ["company", "organization", "company name", "org"],
  "Job Title": ["job title", "title", "position", "role"],
  "Address": ["address", "mailing address", "location"],
  "Classification": ["classification", "lead type", "contact type"],
  "Application in Use": ["application in use", "application", "product interest", "product"],
  "Received Demo": ["received demo", "demo", "demo given", "demo received"],
  "Ranking": ["ranking", "lead ranking", "lead status", "lead rating"],
};

// ─── First pass: auto-map standard fields ───
/**
 * Auto-maps CSV headers to known field names using fuzzy matching.
 * @param {string[]} headers - CSV column headers
 * @returns {Object} Mapping of header → field name
 */
export function autoMapHeaders(headers) {
  const mapping = {};
  const used = new Set();

  EXPECTED_FIELDS.forEach((field) => {
    const variants = STANDARD_FIELD_VARIANTS[field] || [field.toLowerCase()];
    const match = headers.find((h) => {
      if (used.has(h)) return false;
      const hNorm = h.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
      return variants.some(v => hNorm === v || hNorm.includes(v) || v.includes(hNorm));
    });
    if (match) { mapping[field] = match; used.add(match); }
  });

  return mapping;
}

// ─── Second pass: match remaining headers against qualification field csv_column_match ───
export function mapQualificationFields(headers, standardMapping, qualificationFields) {
  const used = new Set(Object.values(standardMapping));
  const qualMapping = {}; // fieldId -> csvColumn

  (qualificationFields || []).forEach(field => {
    if (!field.csv_column_match) return;
    const match = headers.find(h => {
      if (used.has(h)) return false;
      const hNorm = h.toLowerCase().trim();
      return field.csv_column_match.some(variant =>
        hNorm === variant.toLowerCase() || hNorm.includes(variant.toLowerCase()) || variant.toLowerCase().includes(hNorm)
      );
    });
    if (match) {
      qualMapping[field.id] = match;
      used.add(match);
    }
  });

  return qualMapping;
}

// ─── Third pass: auto-detect unmapped columns ───
export function autoDetectNewFields(headers, standardMapping, qualMapping, rows) {
  const used = new Set([...Object.values(standardMapping), ...Object.values(qualMapping)]);
  const unmapped = headers.filter(h => !used.has(h));
  const detected = [];

  unmapped.forEach(col => {
    const values = rows.map(r => (r[col] || "").trim()).filter(Boolean);
    if (values.length === 0) return;

    const unique = [...new Set(values)];

    // Check for yes/no pattern
    const yesNoPatterns = /^(yes|no|y|n|true|false|1|0)$/i;
    if (unique.every(v => yesNoPatterns.test(v))) {
      detected.push({
        csvColumn: col,
        suggestedLabel: col,
        suggestedType: "yes_no",
        suggestedViz: "kpi_rate",
        uniqueValues: unique,
        valueCount: values.length,
        addToMobile: true,
        addToDashboard: true,
      });
      return;
    }

    // Check for rating pattern (all integers 1-5)
    if (unique.every(v => /^[1-5]$/.test(v))) {
      detected.push({
        csvColumn: col,
        suggestedLabel: col,
        suggestedType: "rating",
        suggestedViz: "kpi_rate",
        uniqueValues: unique,
        valueCount: values.length,
        addToMobile: true,
        addToDashboard: true,
      });
      return;
    }

    // Check for dropdown (2-10 unique text values)
    if (unique.length >= 2 && unique.length <= 10) {
      detected.push({
        csvColumn: col,
        suggestedLabel: col,
        suggestedType: "dropdown",
        suggestedViz: "proportion_bar",
        uniqueValues: unique,
        valueCount: values.length,
        addToMobile: true,
        addToDashboard: true,
      });
      return;
    }

    // Everything else = short_text
    detected.push({
      csvColumn: col,
      suggestedLabel: col,
      suggestedType: "short_text",
      suggestedViz: "none",
      uniqueValues: unique.slice(0, 5),
      valueCount: values.length,
      addToMobile: false,
      addToDashboard: false,
    });
  });

  return detected;
}

export function applyMapping(rows, mapping) {
  return rows.map((row) => {
    const m = {};
    EXPECTED_FIELDS.forEach((f) => { const src = mapping[f]; m[f] = src ? (row[src] ?? "") : ""; });
    return m;
  });
}

// ─── Badge Provider Detection ───
const PROVIDER_SIGNATURES = {
  cvent: { name: "Cvent", columns: ["first name", "last name", "email address", "company", "registration type", "attendance status"], minMatch: 4 },
  derseiq: { name: "DerseIQ", columns: ["primary campaign source", "marketing influenced", "ranking", "classification", "application in use"], minMatch: 3 },
  bizzabo: { name: "Bizzabo", columns: ["ticket type", "registration date", "check-in time", "first name", "last name"], minMatch: 3 },
  eventbrite: { name: "Eventbrite", columns: ["order #", "ticket type", "attendee #", "first name", "last name", "email"], minMatch: 4 },
  a2z: { name: "a2z/Map Your Show", columns: ["badge id", "first name", "last name", "company", "booth number"], minMatch: 3 },
};

/**
 * Detects the badge provider (Cvent, Freeman, etc.) from CSV headers.
 * @param {string[]} headers - CSV column headers
 * @returns {string|null} Provider name or null if undetected
 */
export function detectBadgeProvider(headers) {
  const normalized = headers.map(h => h.toLowerCase().trim());
  let bestMatch = null;
  let bestScore = 0;
  Object.entries(PROVIDER_SIGNATURES).forEach(([key, sig]) => {
    const matched = sig.columns.filter(col => normalized.some(h => h.includes(col) || col.includes(h)));
    if (matched.length >= sig.minMatch && matched.length > bestScore) {
      bestScore = matched.length;
      bestMatch = { key, name: sig.name, matchedColumns: matched.length, totalColumns: sig.columns.length };
    }
  });
  return bestMatch;
}

// ─── Saved Mapping Helpers ───
export function getMappingFingerprint(headers) {
  return headers.map(h => h.toLowerCase().trim()).sort().join("|");
}

export function applySavedMapping(headers, savedMappings) {
  const fingerprint = getMappingFingerprint(headers);
  const saved = savedMappings?.find(m => m.fingerprint === fingerprint);
  if (saved) return { mapping: saved.mapping, qualMapping: saved.qualMapping || {}, provider: saved.provider, isExact: true };

  for (const saved of (savedMappings || [])) {
    const savedHeaders = saved.fingerprint.split("|");
    const normalizedNew = headers.map(h => h.toLowerCase().trim());
    const overlap = savedHeaders.filter(sh => normalizedNew.includes(sh));
    if (overlap.length >= savedHeaders.length * 0.8) {
      return { mapping: saved.mapping, qualMapping: saved.qualMapping || {}, provider: saved.provider, isExact: false };
    }
  }

  return null;
}
